import { Container, Graphics, Matrix, Sprite, Text, Texture, type ITextStyle } from 'pixi.js';
import gsap from 'gsap';
import { DropShadowFilter } from '@pixi/filter-drop-shadow';

interface BannerOptions {
  icon?: Texture;
  fillTexture?: string[];
  outlineColor?: number;
  shadowColor?: number;
  textStyle?: Partial<ITextStyle>;
}

export class NotificationBanner extends Container {
  private bg!: Graphics;
  private iconSprite!: Sprite;
  private textLabel!: Text;
  private options: BannerOptions;

  constructor(options?: BannerOptions) {
    super();
    this.options = {
      outlineColor: 0xffffff,
      shadowColor: 0xddcead,
      ...options
    };
    this.initGraphics();
  }

  private initGraphics() {
    // Background
    this.bg = new Graphics();
    this.addChild(this.bg);

    // Icon
    this.iconSprite = new Sprite(this.options.icon);
    this.iconSprite.width = this.iconSprite.height = 24;
    this.iconSprite.x = 16;
    this.iconSprite.y = (9 + 6) / 2;
    this.addChild(this.iconSprite);

    // Text
    this.textLabel = new Text('', {
      fontSize: `18px`,
      fontWeight: '700',
      fill: 0xffffff,
      align: 'center',
      ...this.options.textStyle
    });
    this.textLabel.x = this.iconSprite.x + this.iconSprite.width + 10;
    this.textLabel.y = 6;
    this.textLabel.resolution = devicePixelRatio * 2;
    this.addChild(this.textLabel);

    this.alpha = 0;
    this.visible = false;

    this.updateBackground();
  }

  private updateBackground() {
    const { outlineColor, shadowColor, fillTexture } = this.options;
    this.bg.clear();

    const gradientTexture = createLinearGradientTexture(
      this.iconSprite.width + this.textLabel.width + 16 * 2,
      this.iconSprite.height + 6 * 2,
      fillTexture || ['#777C94', '#191A20']
    );

    this.bg.beginTextureFill({ texture: gradientTexture, matrix: new Matrix() });

    this.bg.lineStyle(1.5, outlineColor);
    this.bg.drawRoundedRect(
      0,
      0,
      this.iconSprite.width + this.textLabel.width + 16 * 2,
      this.iconSprite.height + 6 * 2,
      10
    );
    this.bg.endFill();

    this.filters = [
      new DropShadowFilter({
        color: shadowColor || 0xddcead,
        alpha: 1,
        blur: 8,
        quality: 4,
        resolution: devicePixelRatio,
        pixelSize: [0.5, 0.5],
        offset: { x: 0, y: 0 }
      })
    ];
  }

  setOptions(options: Partial<BannerOptions>) {
    this.options = { ...this.options, ...options };
    if (options.icon) {
      this.iconSprite.texture = options.icon;
    }
    this.updateBackground();
  }

  show(message: string, options?: Partial<BannerOptions>) {
    this.textLabel.text = message;
    if (options) {
      this.setOptions(options);
    }
    this.textLabel.updateText(true);
    this.updateBackground();

    this.visible = true;
    gsap.killTweensOf(this);
    gsap.to(this, { alpha: 1, duration: 0.4, ease: 'power2.out' });
  }

  hide() {
    gsap.killTweensOf(this);
    gsap.to(this, {
      alpha: 0,
      duration: 0.4,
      ease: 'power2.in',
      onComplete: () => {
        this.textLabel.text = '';
        this.textLabel.updateText(true);
        this.visible = false;
      }
    });
  }
}
